// src/components/navigation/BottomNavigation.js

import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Paper, BottomNavigation as MuiBottomNavigation, BottomNavigationAction, Badge, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import HomeIcon from '@mui/icons-material/Home';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

/**
 * Bottom Navigation Component for Mobile
 * 
 * iOS-style bottom navigation bar that appears only on mobile devices (<sm breakpoint).
 * Provides quick access to main sections with icons, labels, and active state highlighting.
 * 
 * Features:
 * - Adaptive navigation based on user role
 * - Safe area padding for iOS/Android notches
 * - Badge support for notifications
 * - Active state with color highlighting
 * - Haptic feedback on navigation
 * - Fixed positioning at bottom
 * 
 * @param {Object} props
 * @param {boolean} props.showBadge - Show notification badge on profile
 * @param {number} props.badgeCount - Number to display in badge
 * @param {string} props.userRole - Current user role (Admin, PartnerAdmin, User)
 * @returns {JSX.Element|null} Bottom navigation bar or null on desktop
 */
const BottomNavigation = ({ showBadge = false, badgeCount = 0, userRole = 'User' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine navigation items based on user role
  const navigationItems = useMemo(() => {
    const baseItems = [
      {
        label: t('navigation.home'),
        value: '/',
        icon: <HomeIcon />,
        roles: ['Admin', 'PartnerAdmin', 'User'],
      },
      {
        label: t('navigation.guides'),
        value: '/guides',
        icon: <MapIcon />,
        roles: ['Admin', 'PartnerAdmin', 'User'],
      },
    ];

    // Admin-specific navigation
    if (userRole === 'Admin') {
      baseItems.push({
        label: t('navigation.admin'),
        value: '/admin',
        icon: <AdminPanelSettingsIcon />,
        roles: ['Admin'],
      });
    }

    // PartnerAdmin-specific navigation
    if (userRole === 'PartnerAdmin') {
      baseItems.push({
        label: t('navigation.partner'),
        value: '/partner',
        icon: <BusinessIcon />,
        roles: ['PartnerAdmin'],
      });
    }

    // Profile - available to all
    baseItems.push({
      label: t('navigation.profile'),
      value: '/profile',
      icon: <PersonIcon />,
      roles: ['Admin', 'PartnerAdmin', 'User'],
      showBadge: showBadge,
      badgeCount: badgeCount,
    });

    return baseItems.filter(item => item.roles.includes(userRole));
  }, [userRole, showBadge, badgeCount, t]);

  // Don't render on desktop
  if (!isMobile) return null;

  // Get current active value based on pathname
  const getCurrentValue = () => {
    const path = location.pathname;
    
    // Exact match first
    const exactMatch = navigationItems.find(item => item.value === path);
    if (exactMatch) return exactMatch.value;
    
    // Prefix match for nested routes
    const prefixMatch = navigationItems.find(item => 
      item.value !== '/' && path.startsWith(item.value)
    );
    if (prefixMatch) return prefixMatch.value;
    
    // Home as fallback
    return '/';
  };

  const handleChange = (event, newValue) => {
    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    
    navigate(newValue);
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: `1px solid ${theme.palette.divider}`,
        // iOS PWA safe area support
        paddingBottom: 'max(env(safe-area-inset-bottom), 8px)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        '@media (display-mode: standalone)': {
          paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
        },
      }}
    >
      <MuiBottomNavigation
        value={getCurrentValue()}
        onChange={handleChange}
        showLabels
        sx={{
          height: 'auto',
          minHeight: 56,
          bgcolor: 'background.paper',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 56,
            maxWidth: 100,
            padding: '6px 8px',
            transition: 'all 0.2s ease',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
              '& .MuiSvgIcon-root': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s ease',
              },
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.5rem',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              marginTop: '2px',
              '&.Mui-selected': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
            },
          },
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            label={item.label}
            value={item.value}
            icon={
              item.showBadge ? (
                <Badge 
                  badgeContent={item.badgeCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: 18,
                      minWidth: 18,
                      padding: '0 4px',
                    },
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
            sx={{
              // Add ripple effect
              '&:active': {
                bgcolor: 'action.selected',
              },
            }}
          />
        ))}
      </MuiBottomNavigation>
    </Paper>
  );
};

export default BottomNavigation;
